import { FC, useCallback, useEffect, useMemo } from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { EasTableWithoutFilter } from 'ui/EasTableWithoutFilter';
import { ButtonCircularProgress } from 'ui/ButtonCircularProgress';
import { ESortDirection } from 'ui/EasTable/types';
import { PAGE_SIZE } from 'services/constants/env';
import {
	clearSpecialTerritoryPostalCodeList,
	createSpecialTerritoryPostalCodeRequest,
	getSpecialTerritoryPostalCodeListRequest,
	resetSaveSpecialTerritoryPostalCodeSuccess,
	updateSpecialTerritoryPostalCodeRequest,
} from 'redux/reducers/taxDuties/reducer';
import {
	getSpecialTerritoryPostalCodeList,
	getSpecialTerritoryPostalCodeListLoading,
	getSpecialTerritoryPostalCodeSaveLoading,
	getSpecialTerritoryPostalCodeSaveSuccess,
} from 'redux/reducers/taxDuties/selectors';
import { TSpecialTerritoryPostalCode } from 'redux/reducers/taxDuties/types';
import { tableStructure } from './tableStructure';
import { TPostalCodeForm, TPostalCodesDialogProps } from './types';

const EDIT_ACTION = 'Edit';

const LIST_VALUES: TPostalCodeForm = { screen: 'list', id: null, postalCode: '' };

const validationSchema = Yup.object({
	postalCode: Yup.string().trim().required('Postal code is required').max(32, 'Max 32 characters'),
});

export const PostalCodesDialog: FC<TPostalCodesDialogProps> = ({ open, onClose, territory }) => {
	const dispatch = useDispatch();

	const list = useSelector(getSpecialTerritoryPostalCodeList);
	const listLoading = useSelector(getSpecialTerritoryPostalCodeListLoading);
	const saveLoading = useSelector(getSpecialTerritoryPostalCodeSaveLoading);
	const saveSuccess = useSelector(getSpecialTerritoryPostalCodeSaveSuccess);

	/**
	 * The screen lives in the form rather than in its own state so that resetting the form is all it
	 * takes to get back to the list — including from the effect that runs once a save went through.
	 * `id` is the edit target: empty on the form screen adds a code, set updates that one.
	 */
	const formik = useFormik<TPostalCodeForm>({
		initialValues: LIST_VALUES,
		validationSchema,
		onSubmit: (values) => {
			if (!territory) return;
			const body = { specialTerritoryId: territory.id, postalCode: values.postalCode.trim() };
			if (values.id) {
				dispatch(updateSpecialTerritoryPostalCodeRequest({ id: values.id, body }));
				return;
			}
			dispatch(createSpecialTerritoryPostalCodeRequest(body));
		},
	});

	/**
	 * The endpoint pages like the top level tables do, but a dialog has no url to page through — the
	 * codes of one territory fit a single generous page.
	 */
	useEffect(() => {
		if (!open || !territory) return;
		dispatch(
			getSpecialTerritoryPostalCodeListRequest({
				pageNumber: 1,
				pageSize: PAGE_SIZE * 10,
				searchString: '',
				filters: [{ fieldName: 'specialTerritoryId', value: String(territory.id), operator: '=' }],
				orderBy: [{ fieldName: 'id', direction: ESortDirection.ASC }],
			}),
		);
	}, [dispatch, open, territory]);

	/** A saved code sends the dialog back to the list, where the refetched row is already waiting. */
	useEffect(() => {
		if (saveSuccess) {
			dispatch(resetSaveSpecialTerritoryPostalCodeSuccess());
			formik.resetForm();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [saveSuccess]);

	/** The list belongs to one territory, so it must not linger for the next one opened. */
	const handleClose = useCallback(() => {
		formik.resetForm();
		dispatch(clearSpecialTerritoryPostalCodeList());
		onClose();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, onClose]);

	const handleAddNew = useCallback(() => {
		formik.setValues({ screen: 'form', id: null, postalCode: '' }).catch(() => {});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handleAction = useCallback(
		(id: number, action: string) => {
			if (action !== EDIT_ACTION) return;
			const item = list?.data?.find((row) => row.id === id);
			formik
				.setValues({ screen: 'form', id: item?.id ?? null, postalCode: item?.postalCode ?? '' })
				.catch(() => {});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[list],
	);

	const items = useMemo(
		() => list ?? { data: [], pageNumber: 1, pageSize: 0, totalCount: 0 },
		[list],
	);

	const isFormScreen = formik.values.screen === 'form';
	const isEdit = !!formik.values.id;
	const territorySuffix = territory?.specialTerritoryName
		? ` — ${territory.specialTerritoryName}`
		: '';

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			sx={{ '& .MuiDialog-paper': { borderRadius: '12px' } }}
		>
			<DialogTitle color="primary">
				{isFormScreen
					? `${isEdit ? 'Edit' : 'Add'} Postal Code${territorySuffix}`
					: `Postal Codes${territorySuffix}`}
			</DialogTitle>

			{isFormScreen ? (
				<>
					<DialogContent sx={{ p: '12px 24px' }}>
						<Box
							component="form"
							onSubmit={formik.handleSubmit}
							sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
						>
							<TextField
								label="Postal Code"
								name="postalCode"
								size="small"
								fullWidth
								autoFocus
								value={formik.values.postalCode}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								onKeyDown={(event) => {
									if (event.key === 'Enter') formik.handleSubmit();
								}}
								error={formik.touched.postalCode && !!formik.errors.postalCode}
								helperText={formik.touched.postalCode && formik.errors.postalCode}
							/>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => formik.resetForm()}>Cancel</Button>
						<Button
							variant="contained"
							disabled={saveLoading}
							onClick={() => formik.handleSubmit()}
							startIcon={saveLoading ? <ButtonCircularProgress /> : undefined}
						>
							{isEdit ? 'Save' : 'Add'}
						</Button>
					</DialogActions>
				</>
			) : (
				<>
					<DialogContent sx={{ p: '12px 24px' }}>
						<Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
							<Button
								variant="contained"
								size="small"
								startIcon={<AddIcon />}
								onClick={handleAddNew}
							>
								Add Postal Code
							</Button>
						</Box>
						<EasTableWithoutFilter<TSpecialTerritoryPostalCode>
							tableStructure={tableStructure}
							items={items}
							itemsIsLoading={listLoading}
							handleAction={handleAction}
						/>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleClose}>Close</Button>
					</DialogActions>
				</>
			)}
		</Dialog>
	);
};
